import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Ditto relay dialect.
 *
 * nostrVM ships with `relay.ditto.pub` as a default relay. Beyond being an
 * event store, Ditto's relay is an engine: it exposes ranked feeds and
 * full-text search via NIP-50 search extensions, and publishes precomputed
 * engagement stats (NIP-85, kind 30383) and trending data (NIP-32 labels,
 * kind 1985) under a dedicated service pubkey.
 *
 * All helpers here are pure parsers/builders. To actually hit Ditto-specific
 * data, target the relay directly — `NPool.query` honours an explicit
 * `{ relays: [DITTO_RELAY] }` and bypasses the configured reqRouter, so these
 * queries don't depend on the user's relay selection.
 */

/** Ditto's flagship relay, also a nostrVM default app relay. */
export const DITTO_RELAY = 'wss://relay.ditto.pub';

/**
 * Service pubkey that publishes Ditto's NIP-85 trusted assertions (kind 30383
 * event stats, kind 30382 user stats) and NIP-32 trending labels (kind 1985).
 */
export const DITTO_STATS_PUBKEY =
  '5f68e85ee174102ca8978eef302129f081f03456c884185d5ec1c1224ab633ea';

/** NIP-32 label namespace under which Ditto publishes all trending data. */
export const DITTO_TRENDS_NAMESPACE = 'pub.ditto.trends';

// ─── NIP-50 search ─────────────────────────────────────────────────────────

/** Ditto-supported NIP-50 sort tokens. */
export type DittoSort = 'hot' | 'top' | 'rising' | 'controversial' | 'zaps' | 'new';

/**
 * Compose a NIP-50 `search` string from a free-text query plus optional Ditto
 * extension tokens. e.g. buildSearch('bitcoin', { sort: 'hot', lang: 'en' })
 * → "bitcoin sort:hot language:en".
 */
export function buildSearch(
  query: string,
  opts: { sort?: DittoSort; lang?: string; distinctAuthor?: boolean } = {},
): string {
  return [
    query.trim(),
    opts.sort ? `sort:${opts.sort}` : '',
    opts.lang ? `language:${opts.lang}` : '',
    opts.distinctAuthor ? 'distinct:author' : '',
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
}

// ─── NIP-85 engagement stats (kind 30383) ──────────────────────────────────

export interface EventStats {
  replyCount: number;
  repostCount: number;
  reactionCount: number;
  zapCount: number;
  zapSats: number;
  /** Ditto's computed rank (0–100), if present. */
  rank?: number;
}

/**
 * Parse a kind 30383 Event Trusted Assertion into engagement counts.
 * Ditto omits zero-valued count tags, so every field defaults to 0.
 */
export function parseEventStats(event: NostrEvent): EventStats {
  const num = (name: string): number => {
    const raw = event.tags.find(([t]) => t === name)?.[1];
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) ? n : 0;
  };
  return {
    replyCount: num('comment_cnt'),
    repostCount: num('repost_cnt') + num('quote_cnt'),
    reactionCount: num('reaction_cnt'),
    zapCount: num('zap_cnt'),
    zapSats: num('zap_amount'),
    rank: event.tags.some(([t]) => t === 'rank') ? num('rank') : undefined,
  };
}

// ─── NIP-32 trending hashtags (kind 1985) ──────────────────────────────────

export interface TrendingHashtag {
  /** The hashtag value, without a leading '#'. */
  tag: string;
  /** Distinct accounts using the tag in the trend window. */
  accounts: number;
  /** Total uses of the tag in the trend window. */
  uses: number;
}

/**
 * Parse trending hashtags from a kind 1985 label event in the
 * `pub.ditto.trends` namespace with the `#t` mark. Rows look like
 * `["t", "<hashtag>", "", "<accounts>", "<uses>"]`. Sorted by uses desc.
 *
 * Note: the same namespace also carries trending notes (`#e`), profiles
 * (`#p`), links (`#r`), and zapped notes (`zapped`) — callers should filter by
 * the `l` mark (`#l: ['#t']`) to get hashtags specifically.
 */
export function parseTrendingHashtags(event: NostrEvent): TrendingHashtag[] {
  return event.tags
    .filter(([name]) => name === 't')
    .map(([, tag, , accounts, uses]) => ({
      tag: tag ?? '',
      accounts: accounts ? parseInt(accounts, 10) || 0 : 0,
      uses: uses ? parseInt(uses, 10) || 0 : 0,
    }))
    .filter((t) => t.tag)
    .sort((a, b) => b.uses - a.uses);
}
